import * as d3Zoom from 'd3-zoom';

import { ToolTypes, Sites, PlaySide } from '@labradorsports/constants';
import SportConfigs from '@labradorsports/sports';
import {
    getSiteForSport,
    objectMerge,
    createException,
    PlayEditorErrors,
    arraySlidingWindow,
    GeneralErrors,
    clamp,
} from '@labradorsports/utils';

import { FieldPiece, FieldViewport, Field, Play, Move, FrameSnapshot } from '../models/index.js';

import { applyOffset, clampZoomTransform, distance, resizeRectangle } from './geometry.js';
import { updateExistingOrCreateNew } from './misc.js';
import { getMoveLength } from './moves.js';

export interface PlayData {
    initial: any[];
    balls: any[];
    frames: any[];
    id: string;
    name: string;
    fieldSetup: boolean;
    viewOptions: any;
    sport: string;
    type: string;
    tags: string[];
    description: string;
    fieldType: string;
    fieldConfig?: any;
    fieldPieces: any[];
    viewers?: any;
    version?: number;
    createdDate?: Date;
    lastUpdated?: Date;
    videoLink?: string;
    branding?: {
        logoEntityId: string;
        colorPrimary: string;
        colorSecondary: string;
    };
    playbookId?: string;
    programId?: string;
    new?: boolean;
    concepts?: PlayConcept[];
    formationId?: string | null;
    formationTags?: string[];
}

export interface RenderedFieldBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface DefaultZoomTransformArgs {
    PlayConfig: PlayConfigSpec;
    play: Play;
    playData: PlayData;
    dimensions: Dimensions;
    zoomLevels: number[];
}

export type DefaultZoomTransformGenerator = (args: DefaultZoomTransformArgs) => d3Zoom.ZoomTransform;

export function doesPlayerTouchBall(playerId: number, balls: any[], frames: any[]): boolean {
    const hasInitialBall = balls.map((ball) => ball.carrier);

    return (
        hasInitialBall.some((id) => id === playerId) ||
        frames.some((frame) => {
            return frame.passes?.some((pass: any) => pass.target === playerId);
        })
    );
}

/**
 * Default placement for piece-type tools: horizontal center of the field; vertically
 * the center of the full field, or for half-field layouts the center of the active
 * (lower) half (75% down the field). Coordinates are clamped to the field.
 */
export function getFieldTypeCenter(
    field: Field,
    fieldType: string | undefined,
    FieldTypes: { HALF_FIELD?: string } | undefined
): FieldPoint {
    const centerX = field.width / 2;
    const isHalfField = fieldType && FieldTypes && fieldType === FieldTypes.HALF_FIELD;
    const centerY = isHalfField ? field.height * 0.75 : field.height / 2;

    return field.clampCoords({ x: centerX, y: centerY });
}

export function doesPieceTouchBall(players: any[], balls: any[], frames: any[], piece: FieldPiece): boolean {
    if (
        piece.type === ToolTypes.BLUEPLAYER ||
        piece.type === ToolTypes.ORANGEPLAYER ||
        piece.type === ToolTypes.COACH
    ) {
        return doesPlayerTouchBall(piece.id, balls, frames);
    }

    const piecePlayers = players.filter((plr) => plr.props?.pieceId === piece.id);

    return (
        piecePlayers.some((plr) => doesPlayerTouchBall(plr.id, balls, frames)) ||
        frames.some((frame) => {
            return frame.shots?.find((shot: any) => shot.target === piece.id);
        })
    );
}

export function initializePlay(PlayConfig: PlayConfigSpec, playData: any): Play {
    const { PlayTypes, FieldTypes, FieldSettings, GoalDesign } = PlayConfig;

    const { sport, fieldType, fieldConfig, type, fieldPieces } = playData;

    const goalDesign = GoalDesign[sport];

    if (fieldType) {
        const defaultConfig = FieldSettings[sport];

        if (!defaultConfig && !fieldConfig) {
            throw createException(PlayEditorErrors.INVALID_FIELD_CONFIG, {
                details: `sport: ${sport} fieldType: ${fieldType} playData: ${JSON.stringify(playData)}`,
            });
        }

        const { width, height, padding, sideline } =
            type === PlayTypes.DRILL && fieldType === FieldTypes.CUSTOM ? fieldConfig : defaultConfig;

        const field = new Field(width, height, padding, sideline, {
            fieldType,
            pieces: fieldPieces,
            goalDesign,
            playerScaleZoomFactor: FieldSettings[sport]?.playerScaleZoomFactor,
        });

        return new Play(PlayConfig, field, playData);
    }

    return new Play(PlayConfig, null, playData);
}

export function createDefaultGoals(PlayConfig: PlayConfigSpec, play: any) {
    const { FieldTypes, FieldSettings, PlayTypes } = PlayConfig;

    if (!play.sport || !play.type || play.fieldType === FieldTypes.CUSTOM) {
        return { pieces: [], goalies: [] };
    }

    const { goals, height } = FieldSettings[play.sport] ?? {};

    const fieldType = play.type === PlayTypes.DRILL ? play.fieldType : play.type;

    if (!goals || !fieldType) {
        throw createException(GeneralErrors.UNKNOWN_ERROR, {
            details: `Missing goals or fieldType ${play.sport} ${play.fieldType} ${play.type}`,
            nestedError: new Error(),
        });
    }

    const pieces =
        goals[fieldType]?.map((goal: any, idx) => ({
            ...goal,
            type: ToolTypes.GOAL,
            id: idx,
            origin: applyOffset(goal.origin, {
                y: play.fieldType === PlayConfig.FieldTypes.HALF_FIELD ? height / 2 : 0,
            }),
        })) ?? [];

    const goalies = pieces.map((goal) => ({
        type: goal.goalieType,
        pieceId: goal.id,
        id: goal.id,
        origin: goal.origin,
    }));

    return { pieces, goalies };
}

export function createDefaultBalls(PlayConfig: PlayConfigSpec, play: any) {
    if (PlayConfig.NewPlaySettings.InitialBalls) {
        return PlayConfig.NewPlaySettings.InitialBalls.map((ballDef, idx) => {
            const player = play.initial.find((plr) => plr.role === ballDef.role);

            return {
                id: idx,
                carrier: player.id,
            };
        });
    }

    return [];
}

export function ensureFootballPlayDefaults(PlayConfig: PlayConfigSpec, play: PlayData) {
    const { PlayerPositions } = SportConfigs[Sites.footballlab];

    if (!play.balls?.length) {
        play.balls = createDefaultBalls(PlayConfig, play);
    }

    const quarterback = play.initial.find((p) => p.role === PlayerPositions.QUARTERBACK);

    if (!quarterback) {
        return;
    }

    play.frames[1] = play.frames[1] ?? getEmptyFrame();
    play.frames[1].passes = [
        {
            type: PlayConfig.ToolTypes.SNAP,
            target: quarterback.id,
            id: 0,
            ball: 0,
        },
    ];
}

export function applyFormationInitial(
    PlayConfig: PlayConfigSpec,
    play: PlayData,
    formation: { initial: any[]; anchorType: string },
    fieldPieces?: any[],
    anchor?: Point
): any[] {
    const pieces = fieldPieces ?? createDefaultGoals(PlayConfig, play).pieces;
    const formationAnchor = anchor ?? getFormationAnchor(PlayConfig, play, formation.anchorType, pieces);

    return formation.initial.map((plr) => ({
        ...plr,
        origin: applyOffset(formationAnchor, plr.origin),
    }));
}

export function createDefaultPlayers(PlayConfig: PlayConfigSpec, play: any): any[] {
    const { NewPlaySettings, FormationDefinitions } = PlayConfig;

    // Defensive plays get the starting defense in addition to the starting offense
    const startingFormationIds = [
        NewPlaySettings.StartingFormation?.[play.sport],
        play.tags?.includes(PlaySide.DEFENSE) ? NewPlaySettings.StartingDefenseFormation?.[play.sport] : null,
    ].filter(Boolean);

    return startingFormationIds
        .flatMap((formationId) => applyFormationInitial(PlayConfig, play, FormationDefinitions[formationId]))
        .map((plr, idx) => ({
            ...plr,
            id: idx,
        }));
}

export function getFieldViewport(players: any[], moves: any[]) {
    let minX = 99999;
    let minY = 99999;
    let maxX = 0;
    let maxY = 0;

    const checkBounds = (item: any) => {
        if (isNaN(item.x) || isNaN(item.y)) {
            return;
        }

        maxX = Math.max(maxX, item.x);
        maxY = Math.max(maxY, item.y);

        minX = Math.min(minX, item.x);
        minY = Math.min(minY, item.y);
    };

    players.forEach((plr) => checkBounds(plr.origin));

    moves.forEach((mv: any) => {
        mv.anchors.forEach(checkBounds);
    });

    const width = maxX - minX;
    const height = maxY - minY;
    const padding = Math.max(height * 0.2, 3);

    return {
        top: minY - padding,
        left: minX - padding,
        width: width + padding * 2,
        height: height + padding * 2,
    };
}

export function createLines(move: Move) {
    return move.anchors.map((anc, idx) => {
        const a = move.anchors[idx - 1] ?? move.origin;

        return {
            a: {
                x: a.x,
                y: a.y,
            },
            b: {
                x: anc.x,
                y: anc.y,
            },
            move,
        };
    });
}

export function convertToUnifiedField(PlayConfig: PlayConfigSpec, play: PlayData) {
    const { PlayTypes, FieldSettings } = PlayConfig;

    const newPlay = {
        ...play,
    };

    if (play.fieldType !== PlayTypes.HALF_FIELD) {
        return newPlay;
    }

    const { height } = FieldSettings[play.sport];
    const offset = { x: 0, y: height / 2 };

    newPlay.initial = newPlay.initial.map((plr: any) => ({
        ...plr,
        origin: applyOffset(plr.origin, offset),
    }));

    if (newPlay.fieldPieces) {
        newPlay.fieldPieces = newPlay.fieldPieces.map((piece) => ({
            ...piece,
            origin: applyOffset(piece.origin, offset),
        }));
    }

    newPlay.frames = newPlay.frames.map((frm: any) => {
        const newFrame = { ...frm };

        if (newFrame.passes) {
            newFrame.passes = newFrame.passes.map((pass) =>
                pass.anchors
                    ? {
                          ...pass,
                          anchors: pass.anchors.map((anc) => ({ ...anc, ...applyOffset(anc, offset) })),
                      }
                    : pass
            );
        }

        if (newFrame.shots) {
            newFrame.shots = newFrame.shots.map((shot) =>
                shot.anchors
                    ? {
                          ...shot,
                          anchors: shot.anchors.map((anc) => ({ ...anc, ...applyOffset(anc, offset) })),
                      }
                    : shot
            );
        }

        if (newFrame.moves) {
            newFrame.moves = newFrame.moves.map((move) =>
                move.anchors
                    ? {
                          ...move,
                          anchors: move.anchors.map((anc) => ({ ...anc, ...applyOffset(anc, offset) })),
                      }
                    : move
            );
        }

        if (newFrame.fieldComments) {
            newFrame.fieldComments = newFrame.fieldComments.map((comment) => ({
                ...comment,
                ...applyOffset(comment, offset),
            }));
        }

        return newFrame;
    });

    return newPlay;
}

// Mutates arguments
export function removePlayerFromFrames(frames: any[], removed: any) {
    frames.forEach((frame: any) => {
        if (frame.passes) {
            frame.passes = frame.passes.filter((pass: any) => pass.target !== removed.id);
        }

        if (frame.shots) {
            frame.shots = frame.shots.filter((shot: any) => shot.target === removed.pieceId);
        }

        if (frame.moves) {
            frame.moves = frame.moves.filter(
                (mv: any) =>
                    mv.target !== removed.id && (mv.targetLine === undefined || mv.targetLine !== removed.pieceId)
            );
        }

        if (frame.assignments) {
            frame.assignments = frame.assignments.filter(
                (asn: any) => asn.defId !== removed.id && asn.atkId !== removed.id
            );
        }

        if (frame.slides) {
            frame.slides = frame.slides.filter((sld: any) => sld.target !== removed.id);
        }

        if (frame.options) {
            if (!removed.hasBall) {
                frame.options = frame.options.filter((opt: any) => opt !== removed.id);
            } else {
                frame.options = [];
            }
        }

        if (frame.routeOptions) {
            frame.routeOptions = frame.routeOptions.filter((ro: any) => ro.source !== removed.id);
        }

        if (frame.playerRotations) {
            frame.playerRotations = frame.playerRotations.filter((rot: any) => rot.target !== removed.id);
        }
    });
}

// Mutates arguments
export function removeBallMovement(play: any, ballId: number) {
    play.balls = play.balls.filter((ball: any) => ball.id !== ballId);

    play.frames.forEach((frame: any) => {
        if (frame.passes) {
            frame.passes = frame.passes.filter((pass: any) => pass.ball !== ballId);
        }

        if (frame.shots) {
            frame.shots = frame.shots.filter((shot: any) => shot.ball !== ballId);
        }
    });
}

export function getEmptyFrame() {
    return {
        moves: [] as any[],
    };
}

export function getDefaultViewOptions(PlayConfig: PlayConfigSpec) {
    return objectMerge(
        {
            ATTACK: {
                players: true,
                moves: true,
                roles: true,
                ids: true,
            },
            DEFENSE: {
                players: true,
                moves: true,
                roles: true,
                ids: true,
                assignments: true,
            },
            flipField: false,
            flipFieldHorizontal: false,
            realField: false,
            fieldLines: {},
            spotlightPlayerIds: [],
            activeConceptId: null,
            comments: true,
            shapes: true,
        },
        PlayConfig.NewPlaySettings.ViewOptions ?? {}
    );
}

export function getNewPlay(PlayConfig: PlayConfigSpec, params: any) {
    const { PlayTypes, NewPlaySettings } = PlayConfig;
    const { type, viewOptions, ...playDetails } = params;

    const play: PlayData = {
        ...playDetails,
        version: 10,
        type,
        fieldSetup: true,
        fieldType: null,
        balls: [],
        initial: [],
        frames: new Array(NewPlaySettings.FrameCount).fill(null).map(() => getEmptyFrame()),
        viewOptions: { ...getDefaultViewOptions(PlayConfig), ...viewOptions },
        concepts: [],
    };

    if (type !== PlayTypes.DRILL) {
        play.fieldType = type;

        const { pieces, goalies } = createDefaultGoals(PlayConfig, play);
        play.fieldPieces = pieces;
        play.initial = goalies.concat(createDefaultPlayers(PlayConfig, play));

        play.balls = createDefaultBalls(PlayConfig, play);
    }

    return play;
}

export function getFrameDuration(PlayConfig: PlayConfigSpec, frame: FrameSnapshot): number {
    if (PlayConfig.Site === Sites.footballlab) {
        // Snap frame should always be short
        if (frame.idx === 1) {
            return PlayConfig.MovementSpeed * 0.5;
        }

        // Empty frame (ex: no pre-snap movement) should always be short
        if (frame.moves?.length === 0) {
            return PlayConfig.MovementSpeed;
        }
    }

    return typeof frame.longestMoveLength === 'number' ? frame.longestMoveLength : 1;
}

function getPlayFieldPositions(play: PlayData): Point[] {
    const piecePositions: Point[] = [];

    play.initial?.forEach((plr) => piecePositions.push(plr.origin));

    play.fieldPieces?.forEach((piece) => piecePositions.push(piece.origin));
    play.frames?.forEach((frame) => {
        if (frame.moves) {
            // Move origin will be covered by player position or previous frame move's final anchor
            frame.moves.forEach((move) => move.anchors?.forEach((anc) => piecePositions.push(anc)));
        }

        // Passes and shots will be covered by passer and receiver moves/locations

        if (frame.fieldComments) {
            frame.fieldComments.forEach((comment) => piecePositions.push(comment));
        }
    });

    return piecePositions.filter((pos) => typeof pos?.x === 'number' && typeof pos?.y === 'number');
}

export function playUsesFullField(play: PlayData, fieldCenter: Point) {
    const piecePositions = getPlayFieldPositions(play);

    return piecePositions.some((pos) => pos?.y < fieldCenter.y);
}

export function getFieldConfig(PlayConfig: PlayConfigSpec, play: PlayData) {
    const { FieldTypes, FieldSettings } = PlayConfig;
    const { sport, fieldType, fieldConfig } = play;

    // Custom field config
    if (fieldType === FieldTypes.CUSTOM) {
        return fieldConfig;
    }

    const fullFieldConfig = FieldSettings[sport];

    const useFullField = fieldType === FieldTypes.FULL_FIELD || playUsesFullField(play, fullFieldConfig.center);

    return {
        ...fullFieldConfig,
        height: useFullField ? fullFieldConfig.height : fullFieldConfig.height / 2,
    };
}

const FOOTBALL_RENDERED_FIELD_MARGIN = 3;
const FOOTBALL_FOCUS_POSITION = 0.66;
const DEFAULT_FOCUS_POSITION = 0.5;

function getFootballQuarterbackPosition(play: PlayData): Point {
    const { PlayerPositions } = SportConfigs[Sites.footballlab];
    return play.initial?.find((plr) => plr.role === PlayerPositions.QUARTERBACK)?.origin;
}

export function getRenderedFieldBounds(PlayConfig: PlayConfigSpec, play: PlayData): RenderedFieldBounds {
    const { FieldTypes, FieldSettings } = PlayConfig;
    const { sport, fieldType } = play;
    const fieldConfig = getFieldConfig(PlayConfig, play);

    if (fieldType === FieldTypes.CUSTOM || getSiteForSport(sport) !== Sites.footballlab) {
        return {
            x: 0,
            y: 0,
            width: fieldConfig.width,
            height: fieldConfig.height,
        };
    }

    const fullFieldConfig = FieldSettings[sport];
    const piecePositions = getPlayFieldPositions(play);

    if (piecePositions.length === 0) {
        return {
            x: 0,
            y: fullFieldConfig.height / 2,
            width: fullFieldConfig.width,
            height: fullFieldConfig.height / 2,
        };
    }

    const minY = Math.max(0, Math.min(...piecePositions.map((pos) => pos.y)) - FOOTBALL_RENDERED_FIELD_MARGIN);
    const maxY = Math.min(
        fullFieldConfig.height,
        Math.max(...piecePositions.map((pos) => pos.y)) + FOOTBALL_RENDERED_FIELD_MARGIN
    );
    const minHeight = 30;
    const quarterbackPosition = getFootballQuarterbackPosition(play);
    const focusY = quarterbackPosition?.y ?? (minY + maxY) / 2;
    const focusPosition = quarterbackPosition ? FOOTBALL_FOCUS_POSITION : DEFAULT_FOCUS_POSITION;
    const heightToFitAboveFocus = (focusY - minY) / focusPosition;
    const heightToFitBelowFocus = (maxY - focusY) / (1 - focusPosition);
    const height = Math.min(
        fullFieldConfig.height,
        Math.max(minHeight, maxY - minY, heightToFitAboveFocus, heightToFitBelowFocus)
    );
    const y = Math.max(0, Math.min(focusY - height * focusPosition, fullFieldConfig.height - height));

    return {
        x: 0,
        y,
        width: fullFieldConfig.width,
        height,
    };
}

export function conformPlayData(PlayConfig: PlayConfigSpec, play: any) {
    const newPlay = { ...play };

    newPlay.initial = newPlay.initial ?? [];
    newPlay.frames = newPlay.frames ?? [];

    if (!newPlay.version) {
        newPlay.version = 2;

        // Properly handle plays which were saved with the old flag name
        newPlay.fieldSetup = Boolean(newPlay.fieldSetup || newPlay.addingInitial);
        delete newPlay.addingInitial;

        if (newPlay.type !== PlayConfig.PlayTypes.DRILL) {
            newPlay.fieldType = newPlay.type;
        }

        // Ensure that field type and goals will always be set (all plays bfor non-drill plays
        const { pieces, goalies } = createDefaultGoals(PlayConfig, newPlay);
        newPlay.fieldPieces = pieces;

        newPlay.initial = newPlay.initial.map((plr: any) => {
            if (plr.type === ToolTypes.ATTACKER) return { ...plr, type: ToolTypes.BLUEPLAYER };
            if (plr.type === ToolTypes.DEFENDER) return { ...plr, type: ToolTypes.ORANGEPLAYER };
            return plr;
        });

        const [defaultGoalie, secondGoalie] = goalies;

        const existingGoalie = newPlay.initial.find((plr: any) => plr.type === ToolTypes.GOALIE);

        // If there is already a goalie, reuse the same id
        newPlay.initial = newPlay.initial.filter((plr: any) => plr.type !== ToolTypes.GOALIE);

        updateExistingOrCreateNew(newPlay.initial, {
            ...defaultGoalie,
            id: existingGoalie ? existingGoalie.id : undefined,
        });

        if (secondGoalie) {
            // Secondary goalie can always get a new id
            updateExistingOrCreateNew(newPlay.initial, {
                ...secondGoalie,
                id: undefined,
            });
        }

        // Ensure that the shoot goes to the primary goal by default
        newPlay.frames = newPlay.frames.map((frm: any) => {
            if (typeof frm.shoot === 'boolean') {
                return { ...frm, shoot: 0 };
            }

            return frm;
        });

        if (!newPlay.viewOptions) {
            newPlay.viewOptions = getDefaultViewOptions(PlayConfig);
        }
    }

    if (newPlay.version === 2) {
        newPlay.version = 3;

        newPlay.frames = newPlay.frames.map((frm: any) => {
            if (typeof frm.pass === 'number') {
                return {
                    ...frm,
                    pass: {
                        type: ToolTypes.PASS,
                        target: frm.pass,
                    },
                };
            }

            return frm;
        });
    }

    if (newPlay.version === 3) {
        newPlay.version = 4;

        newPlay.frames = newPlay.frames.map((frm: any) => {
            return {
                ...frm,
                moves: frm.moves.map((mv: any) => {
                    if (mv.type === PlayConfig.ToolTypes.DODGE || mv.type === PlayConfig.ToolTypes.ROLLBACK) {
                        return {
                            ...mv,
                            type: ToolTypes.OMOVE,
                        };
                    }

                    return mv;
                }),
            };
        });
    }

    if (newPlay.version === 4) {
        newPlay.version = 5;

        newPlay.frames = newPlay.frames.map((frm: any) => {
            const newFrame = {
                ...frm,
            };

            if (newFrame.options) {
                newFrame.options = newFrame.options.map((target: number) => ({
                    type: ToolTypes.OPTION,
                    id: target,
                    target,
                }));
            }

            if (newFrame.shoot !== undefined) {
                newFrame.shoot = {
                    target: newFrame.shoot,
                    type: ToolTypes.SHOOT,
                };
            }

            return newFrame;
        });
    }

    if (newPlay.version === 5) {
        newPlay.version = 6;

        const ballCarrier = newPlay.initial.find((plr: any) => plr.hasBall);

        newPlay.initial = newPlay.initial.map((plr: any) => {
            const newPlr = { ...plr };
            delete newPlr.hasBall;

            return newPlr;
        });

        newPlay.balls = [];

        if (ballCarrier) {
            newPlay.balls.push({
                id: 0,
                carrier: ballCarrier.id,
            });

            // We need to track the holder of the ball to assign source to old options
            let currentHolder = ballCarrier.id;

            newPlay.frames = newPlay.frames.map((frm: any) => {
                const newFrame = {
                    ...frm,
                };

                newFrame.moves = newFrame.moves.map((mv: any) => {
                    if (mv.type === ToolTypes.OMOVE || mv.type === ToolTypes.DMOVE) {
                        return {
                            ...mv,
                            type: ToolTypes.MOVE,
                        };
                    }

                    return mv;
                });

                if (newFrame.options) {
                    newFrame.options = newFrame.options.map((opt: any) => ({
                        ...opt,
                        source: currentHolder,
                    }));
                }

                if (newFrame.pass) {
                    newFrame.passes = [
                        {
                            ...newFrame.pass,
                            ball: 0,
                        },
                    ];

                    currentHolder = newFrame.pass.target;

                    delete newFrame.pass;
                }

                if (newFrame.shoot) {
                    newFrame.shots = [
                        {
                            ...newFrame.shoot,
                            ball: 0,
                        },
                    ];

                    const goalie = play.pieces
                        ? play.initial.find((plr: any) => {
                              if (typeof plr.pieceId === 'number') {
                                  const piece = play.pieces.find((pc: any) => plr.pieceId === pc.id);

                                  return piece.type === ToolTypes.GOAL && piece.id === newFrame.shoot.target;
                              }

                              return false;
                          })
                        : null;

                    if (goalie) {
                        currentHolder = goalie.id;
                    }

                    delete newFrame.shoot;
                }

                return newFrame;
            });
        }
    }

    if (newPlay.version === 6) {
        newPlay.version = 7;

        newPlay.initial = newPlay.initial.map((plr: any) => {
            const newPlr = {
                origin: {
                    x: plr.x,
                    y: plr.y,
                },
                ...plr,
            };

            delete newPlr.x;
            delete newPlr.y;

            return newPlr;
        });
    }

    if (newPlay.version === 7) {
        newPlay.version = 8;

        newPlay.frames = newPlay.frames.map((frm: any) => {
            const newFrame = {
                ...frm,
            };

            if (newFrame.fieldComments) {
                newFrame.fieldComments = newFrame.fieldComments.map((comment) => ({
                    ...comment,
                    width: 150,
                    height: 50,
                }));
            }

            return newFrame;
        });
    }

    if (newPlay.version === 8) {
        newPlay.version = 9;

        Object.assign(newPlay, convertToUnifiedField(PlayConfig, newPlay));
    }

    if (newPlay.version === 9) {
        newPlay.version = 10;

        // Track ball carriers to be able to retrieve appropriate moves
        const ballCarriers = [];
        newPlay.balls.forEach((ball: any) => {
            if (ball.carrier) {
                ballCarriers[ball.id] = ball.carrier;
            }
        });

        newPlay.frames = newPlay.frames.map((frm: any) => {
            const newFrame = {
                ...frm,
            };

            if (newFrame.passes) {
                newFrame.passes = newFrame.passes.map((pass: any) => {
                    const newPass = {
                        ...pass,
                    };

                    if (pass.end) {
                        const receiverMove = newFrame.moves.find((mv: any) => mv.target === pass.target);
                        if (receiverMove) {
                            const player = newPlay.initial.find((plr: any) => plr.id === receiverMove.target);
                            newPass.end =
                                getMoveLength({
                                    ...receiverMove,
                                    origin: player.origin,
                                }) * pass.end;
                        } else {
                            delete newPass.end;
                        }
                    }

                    if (pass.start) {
                        const carrierMove = newFrame.moves.find((mv: any) => mv.target === ballCarriers[pass.ball]);

                        if (carrierMove) {
                            const player = newPlay.initial.find((plr: any) => plr.id === carrierMove.target);
                            newPass.start =
                                getMoveLength({
                                    ...carrierMove,
                                    origin: player.origin,
                                }) * pass.start;
                        } else {
                            delete newPass.start;
                        }
                    }

                    ballCarriers[pass.ball] = pass.target;

                    return newPass;
                });
            }

            return newFrame;
        });
    }

    // Protect against version mismatch (newer play features with older code)
    if (newPlay.initial) {
        newPlay.initial = newPlay.initial.filter((plr: any) => plr.type in PlayConfig.ToolTypes);
    }

    if (newPlay.frames) {
        newPlay.frames = newPlay.frames.map((frame: any) => {
            const newFrame = {
                ...frame,
            };

            if (newFrame.passes) {
                newFrame.passes = newFrame.passes.filter((pass: any) => pass.type in PlayConfig.ToolTypes);
            }

            if (newFrame.shots) {
                newFrame.shots = newFrame.shots.filter((shot: any) => shot.type in PlayConfig.ToolTypes);
            }

            if (newFrame.moves) {
                newFrame.moves = newFrame.moves.filter((mv: any) => mv.type in PlayConfig.ToolTypes);
            }

            return newFrame;
        });
    }

    if (newPlay.pieces) {
        newPlay.pieces = newPlay.pieces.filter((piece: any) => piece.type in PlayConfig.ToolTypes);
    }

    if (!newPlay.concepts) {
        newPlay.concepts = [];
    }

    if (!newPlay.viewOptions) {
        newPlay.viewOptions = getDefaultViewOptions(PlayConfig);
    } else if (newPlay.viewOptions.activeConceptId === undefined) {
        newPlay.viewOptions = {
            ...newPlay.viewOptions,
            activeConceptId: null,
        };
    }

    return newPlay;
}

export function conformFormationData(PlayConfig: PlayConfigSpec, formation: any) {
    const newFormation = { ...formation };

    if (!formation.version) {
        formation.version = 7;

        newFormation.initial.map((plr: any) => {
            const newPlr = { ...plr };
            newPlr.origin = {
                x: plr.x,
                y: plr.y,
            };

            delete newPlr.x;
            delete newPlr.y;

            return newPlr;
        });
    }

    // Protect against version mismatch (newer play features with older code)
    if (newFormation.initial) {
        newFormation.initial = newFormation.initial.filter((plr: any) => plr.type in PlayConfig.ToolTypes);
    }

    if (newFormation.pieces) {
        newFormation.pieces = newFormation.pieces.filter((piece: any) => piece.type in PlayConfig.ToolTypes);
    }

    return newFormation;
}

export function getFormationAnchor(
    PlayConfig: PlayConfigSpec,
    playData: PlayData,
    anchorType: string,
    pieces: any[] = []
) {
    const { FieldTypes, FieldSettings } = PlayConfig;
    const { fieldType, fieldConfig = null, sport } = playData;

    if (fieldType === FieldTypes.CUSTOM) {
        return {
            x: fieldConfig.width / 2,
            y: fieldConfig.height / 2,
        };
    }

    const goals = pieces.filter((piece) => piece.type === ToolTypes.GOAL);

    if (anchorType !== ToolTypes.GOAL || goals.length === 0) {
        return FieldSettings[sport]?.center;
    }

    return goals[0].origin;
}

export function getFieldViewportAttrs(dimensions: Dimensions, field: Field) {
    const { width, height } = dimensions;
    const pixelRatio = width / height;

    const fieldRatio = field.getFullWidth() / field.getFullHeight();

    if (pixelRatio > fieldRatio) {
        const w = height * fieldRatio;
        const h = height;
        const y = 0;
        const x = (width - w) / 2;

        return { w, h, y, x };
    } else {
        const w = width;
        const h = width / fieldRatio;
        const y = (height - h) / 2;
        const x = 0;

        return { w, h, y, x };
    }
}

export function getFieldPaddingPixelRect(fieldViewport: FieldViewport) {
    const { field } = fieldViewport;

    return {
        x: fieldViewport.flippedX
            ? fieldViewport.getPixelX(field.getFullWidth() - field.padding) + 5
            : fieldViewport.getPixelX(-field.padding) + 5,
        y: fieldViewport.flipped
            ? fieldViewport.getPixelY(field.getFullHeight() - field.padding) + 5
            : fieldViewport.getPixelY(-field.padding) + 5,
        width: fieldViewport.getPixelLength(field.getFullWidth()) - 10,
        height: fieldViewport.getPixelLength(field.getFullHeight()) - 10,
        cornerRadius: fieldViewport.getPixelLength(field.padding / 2),
    };
}

export function getZoomStyles(zoomTransform: d3Zoom.ZoomTransform, dragAdjustment: Point = { x: 0, y: 0 }) {
    if (!zoomTransform) {
        return {};
    }

    return {
        width: `${zoomTransform.k * 100}%`,
        height: `${zoomTransform.k * 100}%`,
        left: zoomTransform.x - dragAdjustment.x,
        top: zoomTransform.y - dragAdjustment.y,
    };
}

export function getShadingColor(color: string): string {
    const isTransparent = color?.length === 9 && !color.toLowerCase().endsWith('ff');

    return isTransparent ? color : `${color.slice(0, 7)}38`;
}

export function getDefaultZoomFocus(PlayConfig: PlayConfigSpec, field: Field, players: FieldPiece[]): Point {
    const { FieldTypes, Site } = PlayConfig;
    // Always focus on the first goal

    if (field.fieldType === FieldTypes.CUSTOM) {
        return field.getCenter();
    }

    if (Site === Sites.footballlab) {
        const { PlayerPositions } = SportConfigs[Sites.footballlab];

        const QB = players.find((plr) => plr.props?.role === PlayerPositions.QUARTERBACK);

        if (QB) {
            return QB.origin;
        }

        return field.getCenter();
    }

    if (field.pieces.length > 0) {
        return field.pieces[0].origin;
    }

    return field.getCenter();
}

export function getMediumZoomFactor(dimensions: Dimensions, field: Field): number {
    if (!dimensions) {
        return 2;
    }

    const { w, h } = getFieldViewportAttrs(dimensions, field);
    const widthRatio = dimensions.width / w;
    const heightRatio = dimensions.height / (h / 2);

    return Math.min(widthRatio, heightRatio);
}

export function getZoomLevels(
    PlayConfig: PlayConfigSpec,
    field: Field,
    sport: string,
    dimensions: Dimensions
): number[] {
    if (!field) {
        return [1, 2, 3];
    }

    const { maxZoomFactor = 3 } = PlayConfig.FieldSettings[sport] ?? {};

    return [1, getMediumZoomFactor(dimensions, field), maxZoomFactor];
}

// Default zoom factor below which players shrink with the field, used when a sport doesn't configure its own
const DefaultPlayerScaleZoomFactor = 1;

// Smallest scale players shrink to, so they remain visible and clickable
const PlayerMinScale = 0.35;

// Scale to apply to player glyphs so they shrink proportionally with the field when zoomed out
export function getPlayerScale(fieldViewport: FieldViewport): number {
    if (!fieldViewport) {
        return 1;
    }

    const playerScaleZoomFactor = fieldViewport.field?.playerScaleZoomFactor ?? DefaultPlayerScaleZoomFactor;

    return clamp(fieldViewport.zoomFactor / playerScaleZoomFactor, PlayerMinScale, 1);
}

export function getDefaultZoomFactor(PlayConfig: PlayConfigSpec, play: Play, dimensions: Dimensions): number {
    const { PlayTypes } = PlayConfig;
    const { sport, fieldType } = play ?? {};
    const zoomLevels = getZoomLevels(PlayConfig, play.field, sport, dimensions);

    if (fieldType === PlayTypes.HALF_FIELD) {
        return zoomLevels[1];
    }

    return getSiteForSport(sport) === Sites.footballlab ? zoomLevels[2] : zoomLevels[0];
}

export function getDefaultZoomTransform(
    PlayConfig: PlayConfigSpec,
    fieldViewport: FieldViewport,
    field: Field,
    players: any[],
    zoomFactor: number,
    maxZoomFactor: number
): d3Zoom.ZoomTransform {
    const adjustedFieldViewport = fieldViewport.copy();
    // there is a circular dependency between the fieldViewport and the default zoom transform
    adjustedFieldViewport.zoomFactor = zoomFactor;
    const focus = getDefaultZoomFocus(PlayConfig, field, players); // TODO: remove this
    // Distance from the center line in the x direction
    const focusDiffX = focus ? adjustedFieldViewport.getPixelLength(focus.x - field.width / 2) : 0;
    const focusY = focus ? adjustedFieldViewport.getPixelY(focus.y) : 0;
    const magnification = adjustedFieldViewport.zoomFactor - 1;

    const focusPosition = PlayConfig.Site === Sites.footballlab ? FOOTBALL_FOCUS_POSITION : DEFAULT_FOCUS_POSITION;

    const adjustedFocusPosition = adjustedFieldViewport.flipped ? 1 - focusPosition : focusPosition;
    const offset = {
        x: (focusDiffX - adjustedFieldViewport.w * magnification) / 2,
        y: -(focusY - adjustedFieldViewport.h * adjustedFocusPosition),
    };

    return clampZoomTransform(adjustedFieldViewport, adjustedFieldViewport.zoomFactor, maxZoomFactor, offset);
}

export function getFieldViewportForZoomFactor(play: Play, dimensions: Dimensions, zoomFactor: number): FieldViewport {
    return new FieldViewport(
        play.field,
        play.viewOptions.flipField,
        zoomFactor,
        dimensions,
        play.viewOptions.flipFieldHorizontal
    );
}

export const getDefaultEditorZoomTransform: DefaultZoomTransformGenerator = ({
    PlayConfig,
    play,
    dimensions,
    zoomLevels,
}) => {
    const zoomFactor = getDefaultZoomFactor(PlayConfig, play, dimensions);
    const fieldViewport = getFieldViewportForZoomFactor(play, dimensions, zoomFactor);

    return getDefaultZoomTransform(
        PlayConfig,
        fieldViewport,
        play.field,
        play.players,
        zoomFactor,
        zoomLevels[zoomLevels.length - 1]
    );
};

function getZoomFactorForFieldBounds(
    fieldViewport: FieldViewport,
    bounds: RenderedFieldBounds,
    dimensions: Dimensions
) {
    const widthZoomFactor = dimensions.width / fieldViewport.getPixelLength(bounds.width);
    const heightZoomFactor = dimensions.height / fieldViewport.getPixelLength(bounds.height);

    return Math.min(widthZoomFactor, heightZoomFactor);
}

function getZoomTransformForFieldBounds(
    fieldViewport: FieldViewport,
    bounds: RenderedFieldBounds
): d3Zoom.ZoomTransform {
    const cropX = fieldViewport.flippedX ? bounds.x + bounds.width : bounds.x;
    const cropY = fieldViewport.flipped ? bounds.y + bounds.height : bounds.y;

    return clampZoomTransform(fieldViewport, fieldViewport.zoomFactor, fieldViewport.zoomFactor, {
        x: -fieldViewport.getPixelX(cropX),
        y: -fieldViewport.getPixelY(cropY),
    });
}

export const getDefaultRenderedZoomTransform: DefaultZoomTransformGenerator = ({
    PlayConfig,
    play,
    playData,
    dimensions,
}) => {
    if (getSiteForSport(play.sport) === Sites.footballlab) {
        const bounds = getRenderedFieldBounds(PlayConfig, playData);
        const zoomFactor = getZoomFactorForFieldBounds(
            getFieldViewportForZoomFactor(play, dimensions, 1),
            bounds,
            dimensions
        );
        const fieldViewport = getFieldViewportForZoomFactor(play, dimensions, zoomFactor);

        return getZoomTransformForFieldBounds(fieldViewport, bounds);
    }

    const { height: fieldHeight } = getFieldConfig(PlayConfig, playData);
    const useHalfField = fieldHeight !== play.field.height;
    const zoomFactor = getDefaultZoomFactor(PlayConfig, play, dimensions);
    const fieldViewport = getFieldViewportForZoomFactor(play, dimensions, useHalfField ? zoomFactor : 1);

    return useHalfField
        ? getDefaultZoomTransform(
              PlayConfig,
              fieldViewport,
              play.field,
              play.players,
              fieldViewport.zoomFactor,
              fieldViewport.zoomFactor
          )
        : d3Zoom.zoomIdentity;
};

export function getStraightMoveDuration(move: Move, totalDuration: number, leg: number): number {
    if (!move || move.anchors.length === 1 || leg === -1) {
        return totalDuration;
    }

    const distances = arraySlidingWindow([move.origin, ...move.anchors], distance);
    const totalDist = distances.reduce((acc, dist) => acc + dist, 0);

    return distances.map((dist) => (dist / totalDist) * totalDuration)[leg];
}

export function resizeShading(shading: FieldPiece, anchorStart: Point, anchorEnd: Point): [Point, number, number] {
    return resizeRectangle(
        shading.origin,
        shading.props.width,
        shading.props.height,
        shading.rotation,
        anchorStart,
        anchorEnd,
        typeof shading.props?.moveId === 'number'
    );
}

export function playHasTeam2(play: Play): boolean {
    return play.players.some((player) => {
        if (player.type === ToolTypes.ORANGEPLAYER) {
            if (typeof player.props?.pieceId === 'number') {
                const piece = play.field.getFieldPiece(player.props.pieceId);
                if (piece.type === ToolTypes.GOAL) {
                    return false;
                }
            }

            return true;
        }

        return false;
    });
}

export function getFieldRotation(fieldViewport: FieldViewport, pieceRotation = 0): number {
    return (pieceRotation + (fieldViewport.flipped ? 180 : 0)) * (fieldViewport.flippedHorizontal ? -1 : 1);
}

const defaultPlaybackSpeedSettings: {
    [k: number]: [number, number];
} = {
    1: [4, 200],
    2: [2, 200],
    3: [1, 200],
    4: [0.5, 200],
    5: [0.25, 200],
};

export const PLAYBACK_SPEED_OPTIONS = Object.keys(defaultPlaybackSpeedSettings).map(Number);

/** Relative playback rate labels derived from duration multipliers (e.g. duration×4 → 0.25x). */
export const PLAYBACK_SPEED_LABELS: Record<number, string> = Object.fromEntries(
    Object.entries(defaultPlaybackSpeedSettings).map(([speed, [durationMultiplier]]) => [
        Number(speed),
        `${1 / durationMultiplier}x`,
    ])
);

export interface PlaybackSpeedSettings {
    preFramePause: number;
    frameDuration: number;
    postFramePause: number;
    totalDuration: number;
}

export function getPlaybackSpeedSettings(
    playbackSpeed: number,
    duration = 1,
    debugOptions?: any,
    PlayConfig?: PlayConfigSpec
): PlaybackSpeedSettings {
    const playbackSpeedSettings = defaultPlaybackSpeedSettings;
    const [baseDurationMultiplier, framePause] = playbackSpeedSettings[playbackSpeed];
    const baseTotalDuration = baseDurationMultiplier * 900;

    const debugMovementSpeed = Number(debugOptions?.MOVEMENT_SPEED) / (debugOptions?.SLOW_MOTION ? 5 : 1);
    const movementSpeed = debugMovementSpeed > 0 ? debugMovementSpeed : PlayConfig?.MovementSpeed ?? 10;
    // Prevent frame duration of zero
    const speedAdjustedDuration = Math.max(duration / movementSpeed, 0.1);

    const frameDuration = baseTotalDuration * speedAdjustedDuration;

    return {
        preFramePause: framePause,
        frameDuration,
        postFramePause: framePause,
        totalDuration: frameDuration + framePause * 2,
    };
}
